import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { adminApi, communityApi } from '../lib/api';
import { apiErrorMessage } from '../lib/communityValidation';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
} from '../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { toast } from 'sonner';
import { ArrowLeft, ShieldCheck, ExternalLink, Trash2, Check, X, RotateCcw } from 'lucide-react';
import { format } from 'date-fns';

const LIMIT = 20;

const STATUS_TABS = [
  { value: 'OPEN', label: 'Open' },
  { value: 'RESOLVED', label: 'Resolved' },
  { value: 'DISMISSED', label: 'Dismissed' },
  { value: 'ALL', label: 'All' },
];

const STATUS_BADGE = {
  OPEN: 'bg-amber-500 text-white',
  RESOLVED: 'bg-emerald-600 text-white',
  DISMISSED: 'bg-slate-500 text-white',
};

// Moderation queue for user reports on Community questions/answers. "Delete content"
// removes the reported post (audited server-side) and resolves the report in one step.
const AdminCommunityPage = () => {
  const [status, setStatus] = useState('OPEN');
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [skip, setSkip] = useState(0);
  const [loading, setLoading] = useState(true);
  const [viewItem, setViewItem] = useState(null);
  const [deleteItem, setDeleteItem] = useState(null);
  const [busyId, setBusyId] = useState(null);

  const fetchReports = useCallback(async () => {
    setLoading(true);
    try {
      const data = await adminApi.getCommunityReports(status, skip, LIMIT);
      setItems(data.items);
      setTotal(data.total);
    } catch (err) {
      console.error('Error fetching reports:', err);
      toast.error('Failed to load reports');
    } finally {
      setLoading(false);
    }
  }, [status, skip]);

  useEffect(() => {
    fetchReports();
  }, [fetchReports]);

  const formatDate = (dateStr) => {
    try {
      return format(new Date(dateStr), 'MMM d, yyyy h:mm a');
    } catch {
      return dateStr;
    }
  };

  const setReportStatus = async (report, newStatus, successMsg) => {
    setBusyId(report.report_id);
    try {
      await adminApi.updateCommunityReportStatus(report.report_id, newStatus);
      toast.success(successMsg);
      await fetchReports();
    } catch (err) {
      toast.error(apiErrorMessage(err, 'Failed to update report'));
    } finally {
      setBusyId(null);
    }
  };

  const handleDeleteContent = async () => {
    const report = deleteItem;
    setDeleteItem(null);
    setBusyId(report.report_id);
    try {
      if (report.target_type === 'question') await communityApi.deleteQuestion(report.target_id);
      else await communityApi.deleteAnswer(report.target_id);
      await adminApi.updateCommunityReportStatus(report.report_id, 'RESOLVED');
      toast.success(`${report.target_type === 'question' ? 'Question' : 'Answer'} deleted and report resolved`);
      await fetchReports();
    } catch (err) {
      toast.error(apiErrorMessage(err, 'Failed to delete content'));
    } finally {
      setBusyId(null);
    }
  };

  const from = total === 0 ? 0 : skip + 1;
  const to = Math.min(skip + LIMIT, total);

  return (
    <div className="min-h-[calc(100vh-4rem)] py-8 px-4" data-testid="admin-community-page">
      <div className="max-w-6xl mx-auto">
        <div className="flex items-center gap-4 mb-8">
          <Link to="/admin">
            <Button variant="ghost" size="icon" data-testid="back-to-admin-btn">
              <ArrowLeft className="h-5 w-5" />
            </Button>
          </Link>
          <div className="flex-1 min-w-0">
            <h1 className="font-chivo text-2xl font-black uppercase tracking-tight">Community Moderation</h1>
            <p className="text-muted-foreground text-sm">Questions and answers reported by users</p>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2 mb-6">
          {STATUS_TABS.map((t) => (
            <Button
              key={t.value}
              size="sm"
              variant={status === t.value ? 'default' : 'outline'}
              onClick={() => { setSkip(0); setStatus(t.value); }}
              data-testid={`reports-tab-${t.value.toLowerCase()}`}
            >
              {t.label}
            </Button>
          ))}
          <span className="text-sm text-muted-foreground ml-auto">
            {total === 0 ? '0 reports' : `${from}-${to} of ${total}`}
          </span>
        </div>

        <Card className="border-2">
          <CardContent className="p-0">
            {loading ? (
              <div className="p-6 space-y-3">
                {[1, 2, 3, 4].map((i) => <Skeleton key={i} className="h-10 w-full" />)}
              </div>
            ) : items.length === 0 ? (
              <div className="py-16 text-center" data-testid="no-reports-message">
                <ShieldCheck className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                <p className="text-muted-foreground">
                  {status === 'OPEN' ? 'Nothing to review — the queue is clear.' : 'No reports in this view.'}
                </p>
              </div>
            ) : (
              <Table data-testid="reports-table">
                <TableHeader>
                  <TableRow>
                    <TableHead>Reported</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Reason</TableHead>
                    <TableHead className="min-w-[240px]">Content</TableHead>
                    <TableHead>Author</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {items.map((r) => (
                    <TableRow key={r.report_id} data-testid={`report-row-${r.report_id}`}>
                      <TableCell className="whitespace-nowrap text-sm text-muted-foreground">{formatDate(r.created_at)}</TableCell>
                      <TableCell className="capitalize text-sm">{r.target_type}</TableCell>
                      <TableCell className="text-sm whitespace-nowrap">{r.reason_label}</TableCell>
                      <TableCell className="max-w-xs">
                        <button
                          type="button"
                          className="text-left text-sm text-muted-foreground line-clamp-2 break-words hover:text-foreground"
                          onClick={() => setViewItem(r)}
                          title="View full report"
                        >
                          {r.content_snapshot}
                        </button>
                      </TableCell>
                      <TableCell className="max-w-[140px] truncate text-sm">{r.target_author_name}</TableCell>
                      <TableCell>
                        <div className="flex flex-col gap-1 items-start">
                          <Badge className={`${STATUS_BADGE[r.status]} border-0 text-[10px]`}>{r.status}</Badge>
                          {r.target_deleted && <span className="text-[10px] text-muted-foreground uppercase">Content deleted</span>}
                        </div>
                      </TableCell>
                      <TableCell className="text-right whitespace-nowrap">
                        {!r.target_deleted && (
                          <>
                            <Link
                              to={`/community/${r.question_id}${r.target_type === 'answer' ? `#answer-${r.target_id}` : ''}`}
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              <Button variant="ghost" size="icon" aria-label="Open thread" title="Open thread">
                                <ExternalLink className="h-4 w-4" />
                              </Button>
                            </Link>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="text-destructive hover:text-destructive"
                              disabled={busyId === r.report_id}
                              onClick={() => setDeleteItem(r)}
                              aria-label="Delete reported content"
                              title="Delete reported content"
                              data-testid={`delete-content-${r.report_id}`}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </>
                        )}
                        {r.status === 'OPEN' ? (
                          <>
                            <Button
                              variant="ghost"
                              size="icon"
                              disabled={busyId === r.report_id}
                              onClick={() => setReportStatus(r, 'RESOLVED', 'Report resolved')}
                              aria-label="Mark resolved"
                              title="Mark resolved (action taken)"
                              data-testid={`resolve-report-${r.report_id}`}
                            >
                              <Check className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              disabled={busyId === r.report_id}
                              onClick={() => setReportStatus(r, 'DISMISSED', 'Report dismissed')}
                              aria-label="Dismiss"
                              title="Dismiss (no action needed)"
                              data-testid={`dismiss-report-${r.report_id}`}
                            >
                              <X className="h-4 w-4" />
                            </Button>
                          </>
                        ) : (
                          <Button
                            variant="ghost"
                            size="icon"
                            disabled={busyId === r.report_id}
                            onClick={() => setReportStatus(r, 'OPEN', 'Report reopened')}
                            aria-label="Reopen"
                            title="Reopen"
                          >
                            <RotateCcw className="h-4 w-4" />
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>

        {total > LIMIT && (
          <div className="flex items-center justify-end gap-2 mt-4">
            <Button variant="outline" size="sm" disabled={skip === 0} onClick={() => setSkip((s) => Math.max(0, s - LIMIT))}>
              Previous
            </Button>
            <Button variant="outline" size="sm" disabled={skip + LIMIT >= total} onClick={() => setSkip((s) => s + LIMIT)}>
              Next
            </Button>
          </div>
        )}
      </div>

      {/* Full report */}
      <Dialog open={!!viewItem} onOpenChange={() => setViewItem(null)}>
        <DialogContent className="max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-chivo uppercase">Reported {viewItem?.target_type}</DialogTitle>
            <DialogDescription>
              {viewItem && `${viewItem.reason_label} · reported ${formatDate(viewItem.created_at)} · author: ${viewItem.target_author_name}`}
            </DialogDescription>
          </DialogHeader>
          {viewItem?.details && (
            <div className="text-sm border-l-2 border-amber-500 pl-3 mb-2">
              <span className="font-semibold">Reporter's note: </span>
              <span className="whitespace-pre-wrap break-words">{viewItem.details}</span>
            </div>
          )}
          <p className="text-xs uppercase text-muted-foreground">Content at time of report</p>
          <p className="text-sm whitespace-pre-wrap break-words">{viewItem?.content_snapshot}</p>
        </DialogContent>
      </Dialog>

      {/* Delete content confirmation */}
      <AlertDialog open={!!deleteItem} onOpenChange={() => setDeleteItem(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="font-chivo uppercase">Delete Reported {deleteItem?.target_type}?</AlertDialogTitle>
            <AlertDialogDescription>
              {deleteItem?.target_type === 'question'
                ? 'This permanently deletes the question and all its answers, and resolves this report.'
                : 'This permanently deletes the answer and resolves this report.'}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteContent}
              className="bg-destructive hover:bg-destructive/90"
              data-testid="confirm-delete-content-btn"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default AdminCommunityPage;
